import React from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { ArticleListItem } from '../../../../types/articleListItem.type';
import ArticleActionMenu from './ArticleActionMenu';

interface Props {
    article: ArticleListItem;
    onOpen: (article: ArticleListItem) => void;
    onEdit: (article: ArticleListItem) => void;
    onDuplicate: (article: ArticleListItem) => void;
    onViewUsage: (article: ArticleListItem) => void;
    onAddToWarehouse: (article: ArticleListItem) => void;
    onDelete: (article: ArticleListItem) => void;
}

const initialOf = (name: string) => {
    const trimmed = (name || '').trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

const ArticleRow: React.FC<Props> = ({ article, onOpen, onEdit, onDuplicate, onViewUsage, onAddToWarehouse, onDelete }) => {
    const intl = useIntl();
    const isProduction = !!article.forProduction;

    const typeBadgeLabel = isProduction
        ? intl.formatMessage({ id: 'admin.articles.badge.production', defaultMessage: 'PROD' })
        : intl.formatMessage({ id: 'admin.articles.badge.purchased', defaultMessage: 'BLERË' });

    const contextLabel = isProduction
        ? (article.department?.label || intl.formatMessage({ id: 'admin.articles.row.noDepartment', defaultMessage: '—' }))
        : (article.suppliers?.[0]?.label || intl.formatMessage({ id: 'admin.articles.row.noSupplier', defaultMessage: '—' }));

    const handleRowClick = () => onOpen(article);
    const handleRowKey = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(article);
        }
    };

    return (
        <div
            className="art-row"
            role="button"
            tabIndex={0}
            onClick={handleRowClick}
            onKeyDown={handleRowKey}
        >
            <div className="art-row__thumb">
                {article.image ? (
                    <img src={article.image} alt="" loading="lazy" />
                ) : (
                    <span className="art-row__thumb-initial">{initialOf(article.label)}</span>
                )}
            </div>

            <div className="art-row__main">
                <div className="art-row__name">{article.label}</div>
                <div className="art-row__code">{article.productCode}</div>
            </div>

            <div className="art-row__type">
                <span className={`art-badge art-badge--${isProduction ? 'production' : 'purchased'}`}>
                    {typeBadgeLabel}
                </span>
            </div>

            <div className="art-row__context">
                <span className="art-row__context-value">{contextLabel}</span>
            </div>

            <div className="art-row__meta">
                {isProduction ? (
                    <>
                        <span className="art-row__meta-primary">
                            {article.dailyCapacity != null ? (
                                <FormattedMessage
                                    id="admin.articles.row.capacityPerDay"
                                    defaultMessage="{count}/ditë"
                                    values={{ count: article.dailyCapacity }}
                                />
                            ) : '—'}
                        </span>
                        <span className="art-row__meta-secondary">
                            <FormattedMessage
                                id="admin.articles.row.processCount"
                                defaultMessage="{count, plural, one {# proces} other {# procese}}"
                                values={{ count: article.responsibilitiesCount }}
                            />
                        </span>
                    </>
                ) : (
                    <>
                        <span className="art-row__meta-primary">
                            {article.deliveryDays ? (
                                <FormattedMessage
                                    id="admin.articles.row.deliveryDays"
                                    defaultMessage="{days} ditë"
                                    values={{ days: article.deliveryDays }}
                                />
                            ) : '—'}
                        </span>
                        <span className="art-row__meta-secondary">
                            <FormattedMessage id="admin.articles.row.deliveryLabel" defaultMessage="liferim" />
                        </span>
                    </>
                )}
            </div>

            <div className="art-row__price">
                <span className="art-row__price-primary">
                    {article.pricePerUnit != null ? (
                        <FormattedNumber value={article.pricePerUnit} style="currency" currency="EUR" />
                    ) : '—'}
                </span>
                {article.costPerUnit != null && (
                    <span className="art-row__price-secondary">
                        <FormattedMessage id="admin.articles.row.cogsLabel" defaultMessage="KMSH" />
                        {' '}
                        <FormattedNumber value={article.costPerUnit} style="currency" currency="EUR" />
                    </span>
                )}
            </div>

            <div className="art-row__actions">
                <ArticleActionMenu
                    forProduction={isProduction}
                    onEdit={() => onEdit(article)}
                    onDuplicate={() => onDuplicate(article)}
                    onViewUsage={() => onViewUsage(article)}
                    onAddToWarehouse={() => onAddToWarehouse(article)}
                    onDelete={() => onDelete(article)}
                />
            </div>
        </div>
    );
};

export default ArticleRow;
